import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
    fetchEmpForFinalSalary,
    generateFinalSalary,
    saveFinalSalary,
    clearGeneratedData,
    clearSaveResult,
    resetAll,
    selectEmpListArray,
    selectEmpListLoading,
    selectGeneratedData,
    selectGenerateStatus,
    selectGenerateLoading,
    selectGenerateError,
    selectSaveStatus,
    selectSaveLoading,
    selectSaveError,
} from '../../slices/HRSlice/staffFullFinalSlice';

import {
    User, Building2, Loader2, CheckCircle, AlertCircle,
    RotateCcw, Send, DollarSign, TrendingUp, TrendingDown,
    Calendar, Clock, Award, FileText, ChevronDown, Zap,
    Navigation, BadgeDollarSign, Wallet, Hash,
    Plus, Minus, IndianRupee, X,
} from 'lucide-react';

import {
    GRATUITY_PAY_BY, buildRows, optionalRow, recalcRows, finalTotals, paymentAmount,
    monthNet, validateSettlement, isEarningType, upper,
} from './fullFinalCalc';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (val, dec = 2) => {
    const n = parseFloat(val);
    if (isNaN(n)) return '0.00';
    return n.toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec });
};


// ─── Shared Sub-components ────────────────────────────────────────────────────
const SectionHeader = ({ icon: Icon, title, subtitle, gradient = 'from-indigo-600 to-purple-600' }) => (
    <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100 dark:border-gray-700">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 shadow-md`}>
            <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
            <h3 className="text-base font-bold text-gray-800 dark:text-gray-100">{title}</h3>
            {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
    </div>
);

// Financial summary cards — values come from adjusted totals
const StatCard = ({ label, value, icon: Icon, gradient, sub, delta }) => (
    <div className={`relative overflow-hidden rounded-2xl p-5 text-white bg-gradient-to-br ${gradient} shadow-lg`}>
        <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/4" />
        <div className="relative">
            <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold uppercase tracking-wider text-white/80">{label}</p>
                <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center">
                    <Icon className="h-4 w-4 text-white" />
                </div>
            </div>
            <p className="text-2xl font-black">₹{fmt(value)}</p>
            {delta !== undefined && delta !== 0 && (
                <p className={`text-xs mt-1 font-semibold ${delta > 0 ? 'text-green-200' : 'text-red-200'}`}>
                    {delta > 0 ? '+' : ''}₹{fmt(Math.abs(delta))} from optional heads
                </p>
            )}
            {(!delta || delta === 0) && sub && <p className="text-xs text-white/70 mt-1">{sub}</p>}
        </div>
    </div>
);

const colorMap = {
    indigo:  { border: 'border-indigo-200 dark:border-indigo-800',  bg: 'bg-indigo-50 dark:bg-indigo-900/20',  icon: 'bg-indigo-100 dark:bg-indigo-900/40',  text: 'text-indigo-600 dark:text-indigo-400',  val: 'text-indigo-700 dark:text-indigo-300'  },
    purple:  { border: 'border-purple-200 dark:border-purple-800',  bg: 'bg-purple-50 dark:bg-purple-900/20',  icon: 'bg-purple-100 dark:bg-purple-900/40',  text: 'text-purple-600 dark:text-purple-400',  val: 'text-purple-700 dark:text-purple-300'  },
    violet:  { border: 'border-violet-200 dark:border-violet-800',  bg: 'bg-violet-50 dark:bg-violet-900/20',  icon: 'bg-violet-100 dark:bg-violet-900/40',  text: 'text-violet-600 dark:text-violet-400',  val: 'text-violet-700 dark:text-violet-300'  },
    blue:    { border: 'border-blue-200 dark:border-blue-800',      bg: 'bg-blue-50 dark:bg-blue-900/20',      icon: 'bg-blue-100 dark:bg-blue-900/40',      text: 'text-blue-600 dark:text-blue-400',      val: 'text-blue-700 dark:text-blue-300'      },
    rose:    { border: 'border-rose-200 dark:border-rose-800',      bg: 'bg-rose-50 dark:bg-rose-900/20',      icon: 'bg-rose-100 dark:bg-rose-900/40',      text: 'text-rose-600 dark:text-rose-400',      val: 'text-rose-700 dark:text-rose-300'      },
    amber:   { border: 'border-amber-200 dark:border-amber-800',    bg: 'bg-amber-50 dark:bg-amber-900/20',    icon: 'bg-amber-100 dark:bg-amber-900/40',    text: 'text-amber-600 dark:text-amber-400',    val: 'text-amber-700 dark:text-amber-300'    },
    gray:    { border: 'border-gray-200 dark:border-gray-700',      bg: 'bg-gray-50 dark:bg-gray-900/40',      icon: 'bg-gray-100 dark:bg-gray-700',          text: 'text-gray-400',                         val: 'text-gray-400'                         },
};

const InfoPill = ({ label, value, icon: Icon, color = 'indigo', loading }) => {
    const c = colorMap[value ? color : 'gray'] ?? colorMap['gray'];
    return (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all ${c.border} ${c.bg}`}>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${c.icon}`}>
                {loading
                    ? <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
                    : <Icon className={`h-4 w-4 ${c.text}`} />
                }
            </div>
            <div className="min-w-0">
                <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</p>
                <p className={`text-sm font-bold truncate mt-0.5 ${c.val}`}>
                    {loading ? 'Loading…' : (value || 'N/A')}
                </p>
            </div>
        </div>
    );
};

// ─── Add Optional Head Modal ──────────────────────────────────────────────────
const AddOptionalHeadModal = ({ open, onClose, optionalGroups, optionalAmounts, onChange }) => {
    const [selectedType, setSelectedType] = useState('');
    const [selectedHead, setSelectedHead] = useState('');
    const [amount, setAmount]             = useState('');
    const [added, setAdded]               = useState(false);

    const types         = Object.keys(optionalGroups);
    const headsForType  = selectedType ? (optionalGroups[selectedType] || []) : [];
    const isBen         = isEarningType(selectedType);   // Earning / Benefit / OtherBenefit add to gross (legacy)
    const existingAmt   = selectedHead ? (optionalAmounts[selectedHead] || '') : '';

    const handleTypeChange = (e) => {
        setSelectedType(e.target.value);
        setSelectedHead('');
        setAmount('');
        setAdded(false);
    };

    const handleHeadChange = (e) => {
        const name = e.target.value;
        setSelectedHead(name);
        setAmount(optionalAmounts[name] || '');
        setAdded(false);
    };

    const handleAdd = () => {
        if (!selectedHead || parseFloat(amount) <= 0) return;
        onChange(selectedHead, amount);
        setAdded(true);
        // Reset head & amount for next entry (keep type)
        setTimeout(() => {
            setSelectedHead('');
            setAmount('');
            setAdded(false);
        }, 800);
    };

    const handleClose = () => {
        setSelectedType('');
        setSelectedHead('');
        setAmount('');
        setAdded(false);
        onClose();
    };

    if (!open) return null;

    const canAdd = selectedHead && parseFloat(amount) > 0;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-700 overflow-hidden">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-purple-600 to-violet-600 text-white">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                            <Plus className="h-4 w-4" />
                        </div>
                        <span className="font-bold text-sm">Add Optional Salary Head</span>
                    </div>
                    <button onClick={handleClose}
                        className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-5">

                    {/* Step 1 — Type */}
                    <div>
                        <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Step 1 &mdash; Head Type
                        </label>
                        <div className="relative">
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                            <select value={selectedType} onChange={handleTypeChange}
                                className="w-full appearance-none px-3.5 py-2.5 pr-10 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-purple-500 focus:ring-purple-100 dark:focus:ring-purple-900/30 transition-all">
                                <option value="">— Select Type —</option>
                                {types.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        {selectedType && (
                            <p className={`text-[10px] mt-1.5 font-bold flex items-center gap-1 ${isBen ? 'text-indigo-500' : 'text-rose-500'}`}>
                                {isBen
                                    ? <><Plus className="h-3 w-3" /> Adds to Gross &amp; Net pay</>
                                    : <><Minus className="h-3 w-3" /> Reduces Net pay only</>
                                }
                            </p>
                        )}
                    </div>

                    {/* Step 2 — Head */}
                    {selectedType && (
                        <div>
                            <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                Step 2 &mdash; Salary Head
                            </label>
                            <div className="relative">
                                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                                <select value={selectedHead} onChange={handleHeadChange}
                                    className="w-full appearance-none px-3.5 py-2.5 pr-10 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-purple-500 focus:ring-purple-100 dark:focus:ring-purple-900/30 transition-all">
                                    <option value="">— Select Head —</option>
                                    {headsForType.map(h => (
                                        <option key={h.HeadName} value={h.HeadName}>
                                            {h.HeadName}{optionalAmounts[h.HeadName] > 0 ? ` (₹${fmt(optionalAmounts[h.HeadName])})` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {selectedHead && parseFloat(existingAmt) > 0 && (
                                <p className="text-[10px] mt-1.5 font-semibold text-amber-500">
                                    Currently set to ₹{fmt(existingAmt)} — entering a new value will update it
                                </p>
                            )}
                        </div>
                    )}

                    {/* Step 3 — Amount */}
                    {selectedHead && (
                        <div>
                            <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                Step 3 &mdash; Amount (₹)
                            </label>
                            <div className="relative">
                                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="0.00"
                                    value={amount}
                                    autoFocus
                                    onChange={e => { setAmount(e.target.value); setAdded(false); }}
                                    onKeyDown={e => e.key === 'Enter' && canAdd && handleAdd()}
                                    className="w-full pl-9 pr-3 py-2.5 rounded-xl border-2 text-sm text-right font-bold bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-purple-500 focus:ring-purple-100 transition-all"
                                />
                            </div>
                        </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex items-center gap-3 pt-1">
                        <button onClick={handleClose}
                            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all border border-gray-200 dark:border-gray-600">
                            Done
                        </button>
                        <button onClick={handleAdd} disabled={!canAdd || added}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold shadow-md transition-all disabled:cursor-not-allowed
                                ${added
                                    ? 'bg-green-500 text-white opacity-100'
                                    : 'bg-gradient-to-r from-purple-600 to-violet-600 text-white hover:from-purple-700 hover:to-violet-700 disabled:opacity-50'
                                }`}>
                            {added
                                ? <><CheckCircle className="h-4 w-4" /> Added!</>
                                : <><Plus className="h-4 w-4" /> {parseFloat(existingAmt) > 0 ? 'Update' : 'Add'} Head</>
                            }
                        </button>
                    </div>

                    <p className="text-[10px] text-center text-gray-400">
                        After adding, you can add another head or click Done to close.
                    </p>
                </div>
            </div>
        </div>
    );
};

// ─── Salary table (legacy EmpFinalSalry layout: earnings | deductions side by side) ──
const AmountCell = ({ row, onChange, onRemove }) => (
    <div className="flex items-center justify-end gap-1.5">
        {row.editable ? (
            <input
                type="text"
                inputMode="decimal"
                value={row.amount}
                onChange={e => {
                    const v = e.target.value;
                    if (/^\d*\.?\d{0,2}$/.test(v)) onChange(v);   // legacy ValidateAmount: digits, one dot, 2 decimals
                }}
                className="w-28 px-2 py-1 rounded-lg border-2 border-indigo-200 dark:border-indigo-700 text-right text-sm font-bold bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:outline-none focus:border-indigo-500"
            />
        ) : (
            <span className="text-sm font-bold text-gray-700 dark:text-gray-200">₹{fmt(row.amount)}</span>
        )}
        {row.optional && (
            <button type="button" onClick={onRemove} title="Remove optional head"
                className="w-6 h-6 rounded-md flex items-center justify-center text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30">
                <X className="h-3.5 w-3.5" />
            </button>
        )}
    </div>
);

const HeadLabel = ({ row }) => (
    <div>
        <p className="text-xs font-semibold uppercase text-gray-800 dark:text-gray-100">{row.name}</p>
        {row.optional && <span className="text-[10px] text-purple-500 font-medium">Optional head</span>}
    </div>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const StaffFullFinal = () => {
    const dispatch = useDispatch();
    const { userData } = useSelector(s => s.auth);
    const userName = userData?.userName || userData?.username || 'User';
    const roleId   = userData?.roleId || userData?.RID || 0;

    // ── Selectors ────────────────────────────────────────────────────────────
    const empList         = useSelector(selectEmpListArray);
    const empListLoading  = useSelector(selectEmpListLoading);
    const generatedData   = useSelector(selectGeneratedData);
    const generateStatus  = useSelector(selectGenerateStatus);
    const generateLoading = useSelector(selectGenerateLoading);
    const generateError   = useSelector(selectGenerateError);
    const saveStatus      = useSelector(selectSaveStatus);
    const saveLoading     = useSelector(selectSaveLoading);
    const saveError       = useSelector(selectSaveError);

    // ── Local State ──────────────────────────────────────────────────────────
    const [selectedEmpRefNo, setSelectedEmpRefNo] = useState('');
    const [rows, setRows]                         = useState({ earnings: [], deductions: [], esiPercent: 0 });
    // false = show the server's totals (as legacy until something is edited); true = recalculated here
    const [dirty, setDirty]                       = useState(false);
    const [gratuityPayBy, setGratuityPayBy]       = useState('');
    const [showOptionalModal, setShowOptionalModal] = useState(false);
    const [formErrors, setFormErrors]             = useState([]);

    const d = generatedData;
    const gratuity = parseFloat(d?.Gratuity) || 0;

    // ── Lifecycle ────────────────────────────────────────────────────────────
    useEffect(() => {
        dispatch(fetchEmpForFinalSalary());
        return () => dispatch(resetAll());
    }, [dispatch]);

    // New settlement generated: load its heads; gratuity defaults to Employer (legacy selects the first option)
    useEffect(() => {
        if (generateStatus === 'success' && d) {
            setRows(buildRows(d));
            setDirty(false);
            setFormErrors([]);
            setGratuityPayBy((parseFloat(d.Gratuity) || 0) > 0 ? 'Employeer' : '');
        }
    }, [generateStatus, d]);

    // ── Side-effects: save status ─────────────────────────────────────────────
    useEffect(() => {
        if (saveStatus === 'success') {
            toast.success('Full & Final salary submitted successfully!');
            handleReset();
            dispatch(clearSaveResult());
        } else if (saveStatus === 'failed' && saveError) {
            const msg = typeof saveError === 'string' ? saveError : 'Submission failed. Please try again.';
            setFormErrors(msg.split(',').map(s => s.trim()).filter(Boolean));   // budget errors come comma-separated
            toast.error('Submission failed - see the messages above the Submit button.');
        }
    }, [saveStatus, saveError]); // eslint-disable-line

    // ── Handlers ─────────────────────────────────────────────────────────────
    const resetRows = () => {
        setRows({ earnings: [], deductions: [], esiPercent: 0 });
        setDirty(false);
        setGratuityPayBy('');
        setFormErrors([]);
    };

    const handleEmployeeChange = useCallback((e) => {
        setSelectedEmpRefNo(e.target.value);
        dispatch(clearGeneratedData());
        resetRows();
    }, [dispatch]);

    const handleGenerate = useCallback(() => {
        if (!selectedEmpRefNo) { toast.error('Please select an employee first.'); return; }
        dispatch(generateFinalSalary({ empRefNo: selectedEmpRefNo, createdBy: userName }));
    }, [selectedEmpRefNo, userName, dispatch]);

    const handleReset = useCallback(() => {
        setSelectedEmpRefNo('');
        resetRows();
        dispatch(clearGeneratedData());
        dispatch(clearSaveResult());
    }, [dispatch]);

    // every change re-runs the legacy CountTotal (Paid Leave + ESI recalculated)
    const recalcCtx = useMemo(() => ({
        totalSalaryDays: d?.MonthSalary?.TotalSalaryDays,
        plDays:          d?.MonthSalary?.NoofPLDays,
        plGrossAll:      d?.MonthSalary?.PLGross,
        esiPercent:      rows.esiPercent,
    }), [d, rows.esiPercent]);

    const applyChange = useCallback((update) => {
        setRows(prev => {
            const next = update(prev);
            const r = recalcRows(next.earnings, next.deductions, recalcCtx);
            return { ...next, earnings: r.earnings, deductions: r.deductions };
        });
        setDirty(true);
        setFormErrors([]);
    }, [recalcCtx]);

    const setAmount = (side, index, value) =>
        applyChange(prev => ({ ...prev, [side]: prev[side].map((r, i) => (i === index ? { ...r, amount: value } : r)) }));

    const removeRow = (side, index) =>
        applyChange(prev => ({ ...prev, [side]: prev[side].filter((_, i) => i !== index) }));

    // from the Add Optional Head modal: add the head, or update its amount when already added
    const handleOptionalAmountChange = useCallback((headName, value) => {
        const head = (d?.lstMonthSalaryOptionalHeads || []).find(h => h.HeadName === headName);
        if (!head) return;
        const side = isEarningType(head.HeadType) ? 'earnings' : 'deductions';
        applyChange(prev => {
            const list = prev[side];
            const at = list.findIndex(r => r.optional && r.name === headName);
            const row = optionalRow(head, value);
            return { ...prev, [side]: at >= 0 ? list.map((r, i) => (i === at ? row : r)) : [...list, row] };
        });
    }, [d, applyChange]);

    // ── Derived totals ────────────────────────────────────────────────────────
    const otherEarnings = (parseFloat(d?.BonusBasic) || 0) + (parseFloat(d?.LeaveEncashment) || 0) + gratuity;
    const monthGross = dirty ? rows.earnings.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0) : (parseFloat(d?.MonthSalary?.Gross) || 0);
    const monthDed   = dirty ? rows.deductions.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0) : (parseFloat(d?.MonthSalary?.TotalDeduction) || 0);
    const totals = dirty
        ? finalTotals({ gross: monthGross, deduction: monthDed, otherEarnings })
        : {
            finalGross:     parseFloat(d?.FinalGross) || 0,
            finalDeduction: parseFloat(d?.FinalDeduction) || 0,
            finalNet:       parseFloat(d?.FinalNet) || 0,
            recovery:       parseFloat(d?.RecoverAmt) || 0,
        };
    const payment = paymentAmount({ finalNet: totals.finalNet, gratuity, gratuityPayBy });

    // optional heads not already in the table (legacy hides heads that are present)
    const optionalGroups = useMemo(() => {
        const present = new Set([...rows.earnings, ...rows.deductions].filter(r => !r.optional).map(r => upper(r.name)));
        const groups = {};
        (d?.lstMonthSalaryOptionalHeads || []).forEach(h => {
            if (present.has(upper(h.HeadName))) return;
            (groups[h.HeadType] = groups[h.HeadType] || []).push(h);
        });
        return groups;
    }, [d, rows]);
    const optionalAmounts = useMemo(() => {
        const m = {};
        [...rows.earnings, ...rows.deductions].filter(r => r.optional).forEach(r => { m[r.name] = r.amount; });
        return m;
    }, [rows]);

    const isDW = d?.MonthSalary?.IsDWSalary === 'Yes';
    const tableRows = Math.max(rows.earnings.length, rows.deductions.length);

    const isAlreadyGenerated = generateStatus === 'failed' &&
        typeof generateError === 'string' &&
        generateError.toLowerCase().includes('already generated');

    const selectedEmpName = empList.find(e => e.EmpRefNo === selectedEmpRefNo)?.Name || '';

    // ── Submit (legacy SaveEmpSalary) ─────────────────────────────────────────
    // spUpdateFinalSalary re-applies the heads (comma lists with a trailing comma, walked in lockstep),
    // then spSaveFinalSalary checks / debits the budget. GratuityPayBy = 'Employeer' debits the gratuity
    // from the Gratuity budget; 'Insurance' does not; 'None' when there is no gratuity.
    const handleSave = () => {
        if (!selectedEmpRefNo) return;
        const transactionRefNo = d?.TransactionRefNo || d?.MonthSalary?.TransactionRefNo || '';
        const ccCode           = d?.CCCode || d?.MonthSalary?.JoiningCostCenter || '';
        if (!d || !transactionRefNo) {
            toast.error('Generate the settlement before submitting.');
            return;
        }
        const errors = validateSettlement({ earnings: rows.earnings, deductions: rows.deductions, gratuity, gratuityPayBy });
        setFormErrors(errors);
        if (errors.length > 0) {
            toast.error('Please correct the highlighted fields.');
            return;
        }

        const allHeads = [...rows.earnings, ...rows.deductions];
        const join = (arr) => arr.join(',') + ',';   // SP loop needs a trailing comma
        dispatch(saveFinalSalary({
            empRefNo:          selectedEmpRefNo,
            createdBy:         userName,
            roleId,
            transactionRefNo,
            ccCode,
            payRollFortheDate: d.MonthSalary?.PayRollFortheDate || d.PayRollFortheDate || '',

            monthGross:        +monthGross.toFixed(2),
            monthDeduction:    +monthDed.toFixed(2),
            monthNet:          monthNet(monthGross, monthDed),

            finalGross:        totals.finalGross,
            finalDeduction:    totals.finalDeduction,
            finalNet:          totals.finalNet,
            recoverAmt:        totals.recovery,
            paymentAmount:     payment,
            gratuityPayBy:     gratuity > 0 ? gratuityPayBy : 'None',

            salaryheads:    join(allHeads.map(r => r.name)),
            headtypes:      join(allHeads.map(r => r.type || '')),
            headAmounts:    join(allHeads.map(r => parseFloat(r.amount) || 0)),
            applicablePLs:  join(allHeads.map(r => r.pl || 'No')),
            applicableESIs: join(allHeads.map(r => r.esi || 'No')),
        }));
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            {/* ── Page Header ────────────────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-7 text-white">
                    <div className="absolute inset-0 opacity-10"
                        style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
                    <div className="absolute top-0 right-0 w-72 h-72 bg-violet-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                    <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20">
                                <BadgeDollarSign className="h-7 w-7 text-white" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">HR Module</span>
                                </div>
                                <h1 className="text-2xl md:text-3xl font-black tracking-tight">Staff Full &amp; Final</h1>
                                <p className="text-indigo-200 text-sm mt-0.5">Generate and submit final settlement salary for exited employees</p>
                            </div>
                        </div>
                        <div className="hidden sm:flex items-center gap-3 text-indigo-200">
                            <div className="text-right">
                                <p className="text-xs uppercase tracking-wider">Module</p>
                                <p className="text-sm font-bold text-white">HR / Settlement</p>
                            </div>
                            <Navigation className="h-5 w-5 opacity-60" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto space-y-6">

                {/* ── Employee Selection Card ───────────────────────────────── */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40">
                        <div className="flex items-center gap-3">
                            <BadgeDollarSign className="h-4 w-4 text-indigo-500" />
                            <div>
                                <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">Full &amp; Final Settlement Form</h2>
                                <p className="text-xs text-gray-400 dark:text-gray-500">Select an employee, generate the settlement, then submit</p>
                            </div>
                        </div>
                        <button type="button" onClick={handleReset}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600">
                            <RotateCcw className="h-3.5 w-3.5" /> Reset
                        </button>
                    </div>

                    <div className="p-6 md:p-8 space-y-8">
                        <div>
                            <SectionHeader
                                icon={User}
                                title="Select Employee"
                                subtitle="Choose an employee eligible for full & final settlement"
                                gradient="from-indigo-600 to-purple-600"
                            />
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                        Employee <span className="text-rose-500 ml-0.5">*</span>
                                    </label>
                                    <div className="relative">
                                        {empListLoading
                                            ? <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-indigo-500 pointer-events-none" />
                                            : <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                                        }
                                        <select
                                            value={selectedEmpRefNo}
                                            onChange={handleEmployeeChange}
                                            disabled={empListLoading}
                                            className="w-full appearance-none px-3.5 py-2.5 pr-10 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-300 dark:hover:border-gray-600 transition-all disabled:opacity-60"
                                        >
                                            <option value="">— Select Employee —</option>
                                            {[...new Map(empList.map(emp => [emp.EmpRefNo, emp])).values()].map(emp => (
                                                <option key={emp.EmpRefNo} value={emp.EmpRefNo}>{emp.Name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {empList.length === 0 && !empListLoading && (
                                        <p className="text-[10px] text-amber-500 mt-1 flex items-center gap-1">
                                            <AlertCircle className="h-3 w-3" /> No employees pending settlement found.
                                        </p>
                                    )}
                                </div>

                                {selectedEmpRefNo ? (
                                    <div className="flex items-center gap-4 p-4 rounded-2xl border-2 border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-md shadow-indigo-500/30">
                                            <User className="h-6 w-6 text-white" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-bold text-gray-800 dark:text-gray-100 truncate">{selectedEmpName}</p>
                                            <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-0.5 font-medium">ID: {selectedEmpRefNo}</p>
                                        </div>
                                        <CheckCircle className="h-5 w-5 text-indigo-500 flex-shrink-0" />
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center gap-3 p-4 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/20 text-gray-400">
                                        <User className="h-5 w-5 opacity-40" />
                                        <p className="text-xs font-medium">Employee details will appear here</p>
                                    </div>
                                )}
                            </div>

                            <div className="mt-5 flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={handleGenerate}
                                    disabled={!selectedEmpRefNo || generateLoading}
                                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {generateLoading
                                        ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</>
                                        : <><Zap className="h-4 w-4" /> Generate Settlement</>
                                    }
                                </button>
                                {generateStatus === 'success' && (
                                    <span className="flex items-center gap-1.5 text-xs font-bold text-indigo-600 dark:text-indigo-400">
                                        <CheckCircle className="h-4 w-4" /> Data generated successfully
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Already Generated banner */}
                        {isAlreadyGenerated && (
                            <div className="flex items-start gap-4 p-5 rounded-2xl border-2 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
                                <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0">
                                    <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-amber-700 dark:text-amber-300">Salary Already Generated</p>
                                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                                        The full &amp; final salary for <strong>{selectedEmpName}</strong> has already been generated and is pending approval.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Other error banner */}
                        {generateStatus === 'failed' && !isAlreadyGenerated && generateError && (
                            <div className="flex items-start gap-4 p-5 rounded-2xl border-2 border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20">
                                <div className="w-10 h-10 rounded-xl bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center flex-shrink-0">
                                    <AlertCircle className="h-5 w-5 text-rose-600 dark:text-rose-400" />
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-rose-700 dark:text-rose-300">Generation Failed</p>
                                    <p className="text-xs text-rose-600 dark:text-rose-400 mt-0.5">
                                        {typeof generateError === 'string' ? generateError : 'An error occurred. Please try again.'}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Generated Data ────────────────────────────────────────── */}
                {d && generateStatus === 'success' && (
                    <>
                        {/* ── Employee Info (legacy header) ──────────────────── */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 md:p-8">
                            <SectionHeader icon={User} title="Employee Information" subtitle="Details fetched from the exit record" gradient="from-indigo-600 to-purple-600" />
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                <InfoPill label="Employee Name"            value={d.EmployeeName}    icon={User}      color="indigo" />
                                <InfoPill label="Employee Code"            value={d.EmpRefNo}        icon={Hash}      color="purple" />
                                <InfoPill label="Joined In Service"        value={d.JoiningDate}     icon={Calendar}  color="violet" />
                                <InfoPill label="Resignation Submitted On" value={d.ResignationDate} icon={Calendar}  color="blue" />
                                <InfoPill label="Total Years Worked"       value={d.TotalExp != null ? `${d.TotalExp}` : null} icon={Clock} color="indigo" />
                                <InfoPill label="Cost Center"              value={d.MonthSalary?.CCName || d.CCCode} icon={Building2} color="purple" />
                                <InfoPill label="Relieving Date"           value={d.RelievingDate}   icon={Calendar}  color="violet" />
                                <InfoPill label="Transaction Ref"          value={d.TransactionRefNo} icon={FileText} color="blue" />
                            </div>
                        </div>

                        {/* ── Relieving month salary (editable for daily-wages staff) ── */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 md:p-8">
                            <div className="flex flex-wrap items-center justify-between gap-3 mb-5 pb-4 border-b border-gray-100 dark:border-gray-700">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-blue-600 flex items-center justify-center shadow-md">
                                        <FileText className="h-5 w-5 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-base font-bold text-gray-800 dark:text-gray-100">
                                            Salary For: {d.MonthSalary?.PayRollFortheDate || '—'}
                                        </h3>
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                            Paid Days: {d.MonthSalary?.TotalSalaryDays ?? '—'}
                                            {isDW && ' · Daily wages staff: amounts can be edited; Paid Leave and ESI are recalculated'}
                                        </p>
                                    </div>
                                </div>
                                {(d.lstMonthSalaryOptionalHeads || []).length > 0 && (
                                    <button type="button" onClick={() => setShowOptionalModal(true)}
                                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-gradient-to-r from-purple-600 to-violet-600 text-white shadow-md hover:from-purple-700 hover:to-violet-700">
                                        <Plus className="h-4 w-4" /> Add Optional Head
                                    </button>
                                )}
                            </div>

                            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs uppercase tracking-wider">
                                            <th className="px-4 py-2.5 text-left">Earnings</th>
                                            <th className="px-4 py-2.5 text-right border-r border-white/30">Rs</th>
                                            <th className="px-4 py-2.5 text-left">Deductions</th>
                                            <th className="px-4 py-2.5 text-right">Rs</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                        {Array.from({ length: tableRows }).map((_, i) => {
                                            const e = rows.earnings[i];
                                            const dd = rows.deductions[i];
                                            return (
                                                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                                    <td className="px-4 py-2">{e && <HeadLabel row={e} />}</td>
                                                    <td className="px-4 py-2 border-r border-gray-200 dark:border-gray-700">
                                                        {e && <AmountCell row={e} onChange={v => setAmount('earnings', i, v)} onRemove={() => removeRow('earnings', i)} />}
                                                    </td>
                                                    <td className="px-4 py-2">{dd && <HeadLabel row={dd} />}</td>
                                                    <td className="px-4 py-2">
                                                        {dd && <AmountCell row={dd} onChange={v => setAmount('deductions', i, v)} onRemove={() => removeRow('deductions', i)} />}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-gray-50 dark:bg-gray-900/40 font-bold border-t-2 border-gray-300 dark:border-gray-600">
                                            <td className="px-4 py-2.5 text-xs uppercase">Gross</td>
                                            <td className="px-4 py-2.5 text-right border-r border-gray-200 dark:border-gray-700">₹{fmt(monthGross)}</td>
                                            <td className="px-4 py-2.5 text-xs uppercase">Deduction</td>
                                            <td className="px-4 py-2.5 text-right">₹{fmt(monthDed)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>

                        {/* ── Other Earnings (legacy table) ──────────────────── */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 md:p-8">
                            <SectionHeader icon={Award} title="Other Earnings" subtitle="Bonus, leave encashment and gratuity added to the settlement" gradient="from-violet-600 to-purple-600" />
                            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gradient-to-r from-violet-600 to-purple-600 text-white text-xs uppercase tracking-wider">
                                            <th className="px-4 py-2.5 text-left"></th>
                                            <th className="px-4 py-2.5 text-right">Total Earned Basic</th>
                                            <th className="px-4 py-2.5 text-right">Last Drawn Salary (excl. HRA &amp; DA)</th>
                                            <th className="px-4 py-2.5 text-right">For Days</th>
                                            <th className="px-4 py-2.5 text-right">Per Day</th>
                                            <th className="px-4 py-2.5 text-right">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                        <tr>
                                            <td className="px-4 py-2 font-semibold">Bonus Basic (8.33%)</td>
                                            <td className="px-4 py-2 text-right">₹{fmt(d.TotalBasic)}</td>
                                            <td className="px-4 py-2"></td><td className="px-4 py-2"></td><td className="px-4 py-2"></td>
                                            <td className="px-4 py-2 text-right font-bold">₹{fmt(d.BonusBasic)}</td>
                                        </tr>
                                        <tr>
                                            <td className="px-4 py-2 font-semibold">Leave Encashment</td>
                                            <td className="px-4 py-2"></td>
                                            <td className="px-4 py-2 text-right">₹{fmt(d.LastDrawnSalary)}</td>
                                            <td className="px-4 py-2 text-right">{fmt(d.MonthSalary?.BalanceLeaves, 0)}</td>
                                            <td className="px-4 py-2 text-right">₹{fmt(d.LastDrawnSalaryPerDay)}</td>
                                            <td className="px-4 py-2 text-right font-bold">₹{fmt(d.LeaveEncashment)}</td>
                                        </tr>
                                        {gratuity > 0 && (
                                            <tr>
                                                <td className="px-4 py-2 font-semibold">Gratuity</td>
                                                <td className="px-4 py-2"></td>
                                                <td className="px-4 py-2 text-right">₹{fmt(d.LastDrawnSalary)}</td>
                                                <td className="px-4 py-2 text-right">{fmt(d.Gratuitydays, 0)}</td>
                                                <td className="px-4 py-2 text-right">₹{fmt(d.GratuityPerday)}</td>
                                                <td className="px-4 py-2 text-right font-bold">₹{fmt(gratuity)}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {gratuity === 0 && (
                                <p className="mt-3 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                                    <AlertCircle className="h-3.5 w-3.5" /> No gratuity for this employee (service below the gratuity rule).
                                </p>
                            )}
                        </div>

                        {/* ── Gratuity Pay By (legacy dropdown, required when there is gratuity) ── */}
                        {gratuity > 0 && (
                            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border-2 border-amber-200 dark:border-amber-800 p-6 md:p-8">
                                <SectionHeader icon={Award} title="Gratuity Pay By *" subtitle={`Who releases the gratuity of ₹${fmt(gratuity)}?`} gradient="from-amber-500 to-orange-600" />
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="radiogroup" aria-label="Gratuity Pay By">
                                    {GRATUITY_PAY_BY.map(opt => {
                                        const on = gratuityPayBy === opt.value;
                                        return (
                                            <button key={opt.value} type="button" role="radio" aria-checked={on}
                                                onClick={() => { setGratuityPayBy(opt.value); setFormErrors([]); }}
                                                className={`text-left p-4 rounded-xl border-2 transition-all ${on
                                                    ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 shadow-sm'
                                                    : 'border-gray-200 dark:border-gray-700 hover:border-amber-300'}`}>
                                                <p className="text-sm font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                                                    <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${on ? 'border-amber-500' : 'border-gray-300'}`}>
                                                        {on && <span className="w-2 h-2 rounded-full bg-amber-500" />}
                                                    </span>
                                                    {opt.label}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">{opt.hint}</p>
                                            </button>
                                        );
                                    })}
                                </div>
                                {gratuityPayBy === 'Insurance' && (
                                    <p className="mt-3 text-sm font-semibold text-amber-700 dark:text-amber-300">
                                        Gratuity Released Directly From LIC Rs: {fmt(gratuity)}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* ── Settlement totals (legacy Other Earnings footer) ── */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 md:p-8">
                            <SectionHeader icon={DollarSign} title="Settlement" subtitle={dirty ? 'Recalculated from the amounts above' : 'As generated'} gradient="from-purple-600 to-indigo-600" />
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                                <StatCard label="Total Earnings"   value={totals.finalGross}     icon={TrendingUp}   gradient="from-purple-500 to-indigo-600" sub="Salary + other earnings" />
                                <StatCard label="Total Deductions" value={totals.finalDeduction} icon={TrendingDown} gradient="from-rose-500 to-pink-600"     sub="Relieving month deductions" />
                                <StatCard label="Net Due"          value={totals.finalNet}       icon={Wallet}       gradient="from-indigo-500 to-violet-600" sub="Earnings - deductions" />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="flex items-center justify-between px-5 py-4 rounded-xl border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
                                    <span className="text-xs font-bold uppercase tracking-wide text-green-700 dark:text-green-300">Payment Amount</span>
                                    <span className="text-xl font-black text-green-700 dark:text-green-300">₹{fmt(payment)}</span>
                                </div>
                                <div className={`flex items-center justify-between px-5 py-4 rounded-xl border-2 ${totals.recovery > 0
                                    ? 'border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/20'
                                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30'}`}>
                                    <span className="text-xs font-bold uppercase tracking-wide text-rose-700 dark:text-rose-300">Recovery Amount</span>
                                    <span className="text-xl font-black text-rose-700 dark:text-rose-300">₹{fmt(totals.recovery)}</span>
                                </div>
                            </div>
                        </div>

                        {/* ── Add Optional Head Modal ────────────────────────── */}
                        <AddOptionalHeadModal
                            open={showOptionalModal}
                            onClose={() => setShowOptionalModal(false)}
                            optionalGroups={optionalGroups}
                            optionalAmounts={optionalAmounts}
                            onChange={handleOptionalAmountChange}
                        />

                        {/* ── Submit / Action Bar ────────────────────────────── */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                            {formErrors.length > 0 && (
                                <div className="mb-4 p-4 rounded-xl border-2 border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 text-sm text-rose-700 dark:text-rose-300">
                                    <p className="font-bold mb-1">Please correct the following:</p>
                                    <ul className="list-disc ml-5 space-y-0.5">
                                        {formErrors.map((m, i) => <li key={i}>{m}</li>)}
                                    </ul>
                                </div>
                            )}
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                <div>
                                    <p className="text-sm font-bold text-gray-800 dark:text-gray-100">Ready to submit?</p>
                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                        Payment amount: <span className="font-bold text-indigo-600 dark:text-indigo-400">₹{fmt(payment)}</span>
                                        {gratuity > 0 && <span className="ml-1">· Gratuity by {gratuityPayBy === 'Insurance' ? 'Insurance (LIC)' : gratuityPayBy === 'Employeer' ? 'Employer' : '— not selected'}</span>}
                                    </p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <button type="button" onClick={handleReset}
                                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all border border-gray-200 dark:border-gray-600">
                                        <RotateCcw className="h-4 w-4" /> Reset
                                    </button>
                                    <button type="button" onClick={handleSave} disabled={saveLoading}
                                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                                        {saveLoading
                                            ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>
                                            : <><Send className="h-4 w-4" /> Submit Settlement</>
                                        }
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default StaffFullFinal;
